import { Box, Drawer, Typography, IconButton, Avatar } from "@mui/material";
import { getStringHash } from "../util/helpers";
import { memo, useMemo } from "react";
import ViewsLineChart from "../components/ApexLineChart";
import { ResponsiveContainer } from "recharts";
import { IconSparkles, IconX, IconChartBar, IconTrendingUp, IconEye, IconHeart } from "@tabler/icons-react";

const formatCost = (value) => {
  if (value === 0) return "0.00";
  if (Math.abs(value) >= 0.01) return value.toFixed(2);
  if (Math.abs(value) >= 0.001) return value.toFixed(3);
  return value.toFixed(4);
};

const StatCard = ({ icon, label, value, subtitle }) => (
  <Box
    sx={{
      flex: "1 1 calc(33.33% - 8px)",
      background: "var(--neutral-50)",
      borderRadius: "12px",
      padding: "16px 12px",
      textAlign: "center",
      border: "1px solid var(--neutral-200)",
    }}
  >
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{ mb: 0.5, color: "var(--secondary-500)" }}
    >
      {icon}
    </Box>
    <Typography
      variant="caption"
      sx={{
        color: "var(--body-text-color-500)",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontSize: "0.65rem",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, color: "var(--main-text-color)", mt: 0.25 }}
    >
      {value}
    </Typography>
    {subtitle && (
      <Typography
        variant="caption"
        sx={{ color: "var(--body-text-color-200)", fontSize: "0.7rem" }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

function InsightDrawer({ open, onClose, creator, deliverables = [], conversions = [] }) {
  const sortedStats = useMemo(() => {
    if (!creator?.stats) return [];
    return [...creator.stats].sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [creator?.stats]);

  const metrics = useMemo(() => {
    const totalCost = deliverables.reduce((sum, d) => sum + (parseFloat(d.totalPrice) || 0), 0);
    const latestStats = sortedStats.length > 0 ? sortedStats[sortedStats.length - 1] : null;
    const totalViews = latestStats?.views || 0;
    const totalEngagement = latestStats?.engagement || 0;
    const totalClicks = conversions.reduce((sum, c) => sum + (c.visits || 0), 0);

    return {
      totalCost,
      cpv: totalViews > 0 ? totalCost / totalViews : 0,
      cpe: totalEngagement > 0 ? totalCost / totalEngagement : 0,
      cpc: totalClicks > 0 ? totalCost / totalClicks : 0,
      ctr: totalViews > 0 ? (totalClicks / totalViews) * 100 : 0,
      engagementRate: totalViews > 0 ? (totalEngagement / totalViews) * 100 : 0,
      totalViews,
      totalEngagement,
      totalClicks,
    };
  }, [deliverables, conversions, sortedStats]);

  if (!creator) return null;

  const latestStat = creator?.stats?.length > 0 ? creator.stats.slice(-1)[0] : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      style={{ zIndex: 100000 }}
      PaperProps={{
        sx: {
          background: "#FAFAFE",
          borderLeft: "1px solid var(--neutral-200)",
        },
      }}
    >
      <Box sx={{ minWidth: 340, width: "32vw", maxWidth: 520 }}>
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "linear-gradient(135deg, rgba(159,37,246,0.04) 0%, rgba(92,164,251,0.04) 100%)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid var(--neutral-200)",
            px: 3,
            py: 2,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <IconSparkles size={18} color="var(--secondary-500)" />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  background: "var(--color-gradient)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "0.3px",
                }}
              >
                CREATOR INSIGHTS
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} sx={{ color: "var(--body-text-color-500)" }}>
              <IconX size={18} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: 3, py: 2.5 }}>
          {/* Profile Card */}
          <Box
            sx={{
              background: "#fff",
              borderRadius: "16px",
              border: "1px solid var(--neutral-200)",
              p: 2.5,
              mb: 2.5,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            <Box display="flex" gap={2} alignItems="center">
              <Avatar
                src={
                  creator?.images?.length > 0
                    ? creator?.images[0]
                    : `../gradient${getStringHash(creator?.name, 10)}.png`
                }
                alt={creator.name}
                sx={{
                  width: 64,
                  height: 64,
                  border: "3px solid var(--neutral-100)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
              />
              <Box flex={1}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "var(--main-text-color)", lineHeight: 1.3 }}
                >
                  {creator?.name}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Overview */}
          <Box sx={{ mb: 2.5 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <IconChartBar size={16} color="var(--secondary-500)" />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "var(--main-text-color)", letterSpacing: "0.2px" }}
              >
                Overview
              </Typography>
            </Box>
            <Box
              sx={{
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid var(--neutral-200)",
                p: 2.5,
              }}
            >
              {!latestStat && (
                <Typography variant="body2" sx={{ color: "var(--body-text-color-500)", textAlign: "center", py: 1 }}>
                  No stats available yet
                </Typography>
              )}
              {latestStat && (
                <Box display="flex" gap="8px" flexWrap="wrap">
                  <StatCard
                    icon={<IconHeart size={18} />}
                    label="Engagement"
                    value={latestStat.engagement ?? "—"}
                  />
                  <StatCard
                    icon={<IconHeart size={18} />}
                    label="Likes"
                    value={latestStat.likes ?? "—"}
                  />
                  <StatCard
                    icon={<IconTrendingUp size={18} />}
                    label="Shares"
                    value={latestStat.shares ?? "—"}
                  />
                  <StatCard
                    icon={<IconChartBar size={18} />}
                    label="Comments"
                    value={latestStat.comments ?? "—"}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Performance Metrics */}
          <Box sx={{ mb: 2.5 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <IconTrendingUp size={16} color="var(--secondary-500)" />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "var(--main-text-color)", letterSpacing: "0.2px" }}
              >
                Performance Metrics
              </Typography>
            </Box>
            <Box display="flex" gap="8px" flexWrap="wrap">
              <StatCard
                icon={<IconEye size={18} />}
                label="CPV"
                value={metrics.totalCost > 0 ? `$${formatCost(metrics.cpv)}` : "—"}
              />
              <StatCard
                icon={<IconHeart size={18} />}
                label="CPE"
                value={metrics.totalCost > 0 ? `$${formatCost(metrics.cpe)}` : "—"}
              />
              <StatCard
                icon={<IconTrendingUp size={18} />}
                label="CPC"
                value={metrics.totalCost > 0 && metrics.totalClicks > 0 ? `$${formatCost(metrics.cpc)}` : "—"}
              />
              <StatCard
                icon={<IconChartBar size={18} />}
                label="CTR"
                value={metrics.totalClicks > 0 ? `${metrics.ctr.toFixed(2)}%` : "—"}
              />
              <Box
                sx={{
                  flex: "1 1 100%",
                  background: "var(--neutral-50)",
                  borderRadius: "12px",
                  padding: "16px 12px",
                  textAlign: "center",
                  border: "1px solid var(--neutral-200)",
                }}
              >
                <Box display="flex" justifyContent="center" sx={{ mb: 0.5, color: "var(--secondary-500)" }}>
                  <IconSparkles size={18} />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--body-text-color-500)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontSize: "0.65rem",
                    fontWeight: 600,
                  }}
                >
                  Engagement Rate
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--main-text-color)", mt: 0.25 }}>
                  {metrics.totalViews > 0 ? `${metrics.engagementRate.toFixed(2)}%` : "—"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Views Chart */}
          <Box sx={{ mb: 5 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <IconEye size={16} color="var(--secondary-500)" />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "var(--main-text-color)", letterSpacing: "0.2px" }}
              >
                Views
              </Typography>
            </Box>
            <Box
              sx={{
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid var(--neutral-200)",
                p: 2,
                overflow: "hidden",
              }}
            >
              <ResponsiveContainer width="100%" height={280}>
                <ViewsLineChart
                  height={280}
                  style={{ width: "100%" }}
                  data={[
                    sortedStats.map((stat) => ({ views: stat.views })),
                    sortedStats.map((stat) => stat.date),
                  ]}
                  dataKey="views"
                />
              </ResponsiveContainer>
            </Box>
          </Box>

          {/* Engagement Chart */}
          <Box sx={{ mt: 1, mb: 2.5 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <IconHeart size={16} color="var(--secondary-500)" />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "var(--main-text-color)", letterSpacing: "0.2px" }}
              >
                Engagement
              </Typography>
            </Box>
            <Box
              sx={{
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid var(--neutral-200)",
                p: 2,
                overflow: "hidden",
              }}
            >
              <ResponsiveContainer width="100%" height={280}>
                <ViewsLineChart
                  height={280}
                  style={{ width: "100%" }}
                  data={[
                    sortedStats.map((stat) => ({ engagement: stat.engagement })),
                    sortedStats.map((stat) => stat.date),
                  ]}
                  dataKey="engagement"
                />
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

export default memo(InsightDrawer);
